import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, Check, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";
import LockCountdownChip from "@/components/LockCountdownChip";

const API = process.env.REACT_APP_BACKEND_URL;

// Live corridors map to the /api/rates pairs we actively route today.
// Each Active entry can optionally include a `slug` linking to its detail page.
const ACTIVE = [
  { flag: "🇺🇸", name: "United States", code: "US", slug: "usd-ngn" },
  { flag: "🇬🇧", name: "United Kingdom", code: "GB", slug: "gbp-kes" },
  { flag: "🇮🇪", name: "Ireland", code: "IE", slug: "eur-ghs" },
  { flag: "🇳🇱", name: "Netherlands", code: "NL", slug: "eur-ghs" },
  { flag: "🇩🇪", name: "Germany", code: "DE", slug: "eur-ghs" },
  { flag: "🇫🇷", name: "France", code: "FR", slug: "eur-ghs" },
  { flag: "🇪🇸", name: "Spain", code: "ES", slug: "eur-mad" },
  { flag: "🇮🇹", name: "Italy", code: "IT", slug: "eur-ghs" },
  { flag: "🇧🇪", name: "Belgium", code: "BE", slug: "eur-ghs" },
  { flag: "🇵🇹", name: "Portugal", code: "PT", slug: "eur-ghs" },
  { flag: "🇳🇬", name: "Nigeria", code: "NG", slug: "usd-ngn" },
  { flag: "🇬🇭", name: "Ghana", code: "GH", slug: "eur-ghs" },
  { flag: "🇰🇪", name: "Kenya", code: "KE", slug: "gbp-kes" },
  { flag: "🇿🇦", name: "South Africa", code: "ZA", slug: "cad-zar" },
  { flag: "🇸🇳", name: "Senegal", code: "SN", slug: "usd-xof" },
  { flag: "🇨🇲", name: "Cameroon", code: "CM", slug: "usd-xof" },
  { flag: "🇪🇹", name: "Ethiopia", code: "ET", slug: "usd-ngn" },
];

const SOON = [
  { flag: "🇨🇦", name: "Canada", code: "CA", phase: "Q1 2027" },
  { flag: "🇦🇺", name: "Australia", code: "AU", phase: "Q1 2027" },
  { flag: "🇨🇭", name: "Switzerland", code: "CH", phase: "Q1 2027" },
  { flag: "🇸🇪", name: "Sweden", code: "SE", phase: "Q1 2027" },
  { flag: "🇳🇴", name: "Norway", code: "NO", phase: "Q1 2027" },
  { flag: "🇩🇰", name: "Denmark", code: "DK", phase: "Q1 2027" },
  { flag: "🇫🇮", name: "Finland", code: "FI", phase: "Q1 2027" },
  { flag: "🇦🇹", name: "Austria", code: "AT", phase: "Q1 2027" },
  { flag: "🇦🇪", name: "UAE", code: "AE", phase: "Q1 2027" },
  { flag: "🇸🇦", name: "Saudi Arabia", code: "SA", phase: "Q1 2027" },
  { flag: "🇮🇳", name: "India", code: "IN", phase: "H2 2027" },
  { flag: "🇵🇰", name: "Pakistan", code: "PK", phase: "H2 2027" },
  { flag: "🇧🇩", name: "Bangladesh", code: "BD", phase: "H2 2027" },
  { flag: "🇵🇭", name: "Philippines", code: "PH", phase: "H2 2027" },
  { flag: "🇮🇩", name: "Indonesia", code: "ID", phase: "H2 2027" },
  { flag: "🇻🇳", name: "Vietnam", code: "VN", phase: "H2 2027" },
  { flag: "🇹🇭", name: "Thailand", code: "TH", phase: "H2 2027" },
  { flag: "🇲🇾", name: "Malaysia", code: "MY", phase: "H2 2027" },
  { flag: "🇸🇬", name: "Singapore", code: "SG", phase: "H2 2027" },
  { flag: "🇯🇵", name: "Japan", code: "JP", phase: "H2 2027" },
  { flag: "🇰🇷", name: "South Korea", code: "KR", phase: "H2 2027" },
  { flag: "🇨🇳", name: "China", code: "CN", phase: "H2 2027" },
  { flag: "🇹🇷", name: "Turkey", code: "TR", phase: "H2 2027" },
  { flag: "🇧🇷", name: "Brazil", code: "BR", phase: "H2 2027" },
  { flag: "🇲🇽", name: "Mexico", code: "MX", phase: "H2 2027" },
  { flag: "🇦🇷", name: "Argentina", code: "AR", phase: "2028" },
  { flag: "🇨🇴", name: "Colombia", code: "CO", phase: "2028" },
  { flag: "🇪🇬", name: "Egypt", code: "EG", phase: "Q1 2027" },
  { flag: "🇲🇦", name: "Morocco", code: "MA", phase: "Q1 2027" },
  { flag: "🇹🇳", name: "Tunisia", code: "TN", phase: "H2 2027" },
  { flag: "🇹🇿", name: "Tanzania", code: "TZ", phase: "Q1 2027" },
  { flag: "🇺🇬", name: "Uganda", code: "UG", phase: "Q1 2027" },
  { flag: "🇷🇼", name: "Rwanda", code: "RW", phase: "Q1 2027" },
  { flag: "🇨🇮", name: "Côte d'Ivoire", code: "CI", phase: "Q1 2027" },
  { flag: "🇿🇲", name: "Zambia", code: "ZM", phase: "H2 2027" },
  { flag: "🇿🇼", name: "Zimbabwe", code: "ZW", phase: "H2 2027" },
  { flag: "🇦🇴", name: "Angola", code: "AO", phase: "2028" },
  { flag: "🇧🇼", name: "Botswana", code: "BW", phase: "2028" },
  { flag: "🇳🇦", name: "Namibia", code: "NA", phase: "2028" },
  { flag: "🇲🇿", name: "Mozambique", code: "MZ", phase: "2028" },
];

// Deterministic "transfers today" per country — pseudo-random but stable for a day.
function transfersToday(code) {
  const day = Math.floor(Date.now() / 86400000);
  let h = 0;
  const s = code + ":" + day;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return 800 + (Math.abs(h) % 5200); // 800–6000 range
}

export default function AvailableCorridors() {
  const { t } = useTranslation();
  const { prefix } = useLang();
  const [voted, setVoted] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("ix.corridor.votes") || "[]"));
    } catch (_) {
      return new Set();
    }
  });
  const [top, setTop] = useState([]);
  const [myCountry, setMyCountry] = useState(null);
  const [voteCounts, setVoteCounts] = useState({});
  const [threshold, setThreshold] = useState(50);
  const [launched, setLaunched] = useState(() => new Set());

  // Auto-highlight + top-5 + threshold + launched-corridor overrides
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/geo`)
      .then((r) => r.json())
      .then((d) => alive && d?.country && setMyCountry(d.country.toUpperCase()))
      .catch(() => {});
    fetch(`${API}/api/corridors/votes/top?limit=20`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        setTop((d?.top || []).slice(0, 5));
        const counts = {};
        (d?.top || []).forEach((t) => {
          counts[t.country] = t.count;
        });
        setVoteCounts(counts);
      })
      .catch(() => {});
    fetch(`${API}/api/corridors/vote-threshold`)
      .then((r) => r.json())
      .then((d) => alive && d?.threshold && setThreshold(d.threshold))
      .catch(() => {});
    fetch(`${API}/api/corridors/launched`)
      .then((r) => r.json())
      .then((d) => alive && setLaunched(new Set((d?.countries || []).map((c) => String(c).toUpperCase()))))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const castVote = async (country) => {
    if (voted.has(country.code)) return;
    const next = new Set(voted);
    next.add(country.code);
    setVoted(next);
    // Optimistically bump the count locally
    setVoteCounts((prev) => ({
      ...prev,
      [country.code]: (prev[country.code] || 0) + 1,
    }));
    try {
      localStorage.setItem("ix.corridor.votes", JSON.stringify([...next]));
    } catch (_) { /* ignore */ }
    try {
      const r = await fetch(`${API}/api/corridors/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country: country.code,
          name: country.name,
          flag: country.flag,
        }),
      });
      const d = await r.json();
      if (d?.count !== undefined) {
        setVoteCounts((prev) => ({ ...prev, [country.code]: d.count }));
      }
      const r2 = await fetch(`${API}/api/corridors/votes/top?limit=5`);
      const d2 = await r2.json();
      setTop(d2?.top || []);
    } catch (_) { /* silent */ }
  };

  const all = useMemo(() => {
    const promoted = [];
    const stillSoon = [];
    SOON.forEach((c) => {
      if (launched.has(c.code)) promoted.push(c);
      else stillSoon.push(c);
    });
    return [
      ...ACTIVE.map((c) => ({ ...c, active: true })),
      ...promoted.map((c) => ({ ...c, active: true, justLaunched: true })),
      ...stillSoon.map((c) => ({ ...c, active: false })),
    ];
  }, [launched]);

  return (
    <section
      id="available-corridors"
      className="relative py-24 md:py-32 bg-forest text-canvas noise overflow-hidden"
      data-testid="available-corridors"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(600px 300px at 85% 10%, rgba(232,255,0,0.10), transparent 60%), radial-gradient(500px 250px at 10% 90%, rgba(124,58,237,0.35), transparent 60%)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12 md:mb-16">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-bold text-lime">
              <Sparkles className="w-4 h-4" strokeWidth={2} />
              {t("corridors.eyebrow")}
            </div>
            <h2 className="mt-4 font-display font-medium text-canvas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              {t("corridors.headline")}
              <br />
              <span className="italic font-serif text-lime">{t("corridors.headlineItalic")}</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-canvas/70 leading-relaxed">
            {t("corridors.intro")}
            <span className="text-lime">{t("corridors.introHighlight")}</span>
          </div>
        </div>

        {/* Most requested — live, community-driven */}
        {top.length > 0 && (
          <div
            className="mb-8 rounded-2xl border border-lime/30 bg-lime/[0.06] p-4 md:p-5 flex flex-wrap items-center gap-3"
            data-testid="corridors-top-requested"
          >
            <div className="flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase font-bold text-lime">
              <TrendingUp className="w-4 h-4" strokeWidth={2} />
              {t("corridors.mostRequested")}
            </div>
            <div className="flex flex-wrap gap-2">
              {top.map((tt) => (
                <span
                  key={tt.country}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-canvas/8 border border-canvas/15 text-canvas text-xs"
                  data-testid={`top-${tt.country}`}
                >
                  <span aria-hidden>{tt.flag}</span>
                  {tt.name}
                  <span className="text-lime font-mono ml-1">{tt.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Counts strip */}
        <div className="mb-10 flex flex-wrap items-center gap-6 pb-6 border-b border-canvas/12">
          <div className="flex items-center gap-2.5" data-testid="corridors-active-count">
            <span className="w-2 h-2 rounded-full bg-lime shadow-[0_0_12px_rgba(232,255,0,0.7)]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-canvas">
              {all.filter((c) => c.active).length} {t("corridors.active")}
            </span>
          </div>
          <div className="flex items-center gap-2.5" data-testid="corridors-soon-count">
            <span className="w-2 h-2 rounded-full bg-canvas/40" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-canvas/70">
              {all.filter((c) => !c.active).length} {t("corridors.comingSoon")}
            </span>
          </div>
          <div className="ml-auto text-[11px] tracking-[0.22em] uppercase text-canvas/50">
            {t("corridors.tapToVote")}
          </div>
        </div>

        {/* Grid */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3"
          data-testid="corridors-grid"
        >
          {all.map((c, i) => {
            const isMine = myCountry === c.code;
            const alreadyVoted = voted.has(c.code);
            const tile = (
              <>
                {isMine && (
                  <span
                    aria-hidden
                    className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded bg-lime text-ink text-[9px] font-bold tracking-[0.18em] uppercase shadow-[0_0_18px_rgba(232,255,0,0.7)] animate-pulse"
                  >
                    {t("corridors.you")}
                  </span>
                )}
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={
                      "text-2xl md:text-[26px] leading-none " +
                      (c.active ? "" : "opacity-40 grayscale")
                    }
                    aria-hidden
                  >
                    {c.flag}
                  </span>
                  <span
                    className={
                      "text-[9px] tracking-[0.18em] uppercase font-bold px-1.5 py-0.5 rounded " +
                      (c.active
                        ? "bg-lime text-ink"
                        : alreadyVoted
                          ? "bg-lime/20 border border-lime/40 text-lime"
                          : "border border-canvas/25 text-canvas/55")
                    }
                  >
                    {c.active ? t("corridors.activePill") : alreadyVoted ? t("corridors.votedPill") : t("corridors.soonPill")}
                  </span>
                </div>
                <div
                  className={
                    "mt-3 font-display tracking-tight leading-tight " +
                    (c.active ? "text-canvas" : "text-canvas/60")
                  }
                >
                  {c.name}
                </div>
                {c.active ? (
                  <>
                    <div className="mt-1 text-[10px] font-mono tracking-widest text-lime/85">
                      {transfersToday(c.code).toLocaleString()} {t("corridors.sentToday")}
                    </div>
                    {c.slug && (
                      <LockCountdownChip
                        slug={c.slug}
                        tone="dark"
                        label={t("rateLock.countdown")}
                        timeLabels={{
                          hours: t("rateLock.hours"),
                          minutes: t("rateLock.minutes"),
                          seconds: t("rateLock.seconds"),
                        }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="font-mono text-[10px] tracking-widest text-canvas/40">
                        {c.code}
                      </span>
                      {c.phase && (
                        <span
                          className="text-[9px] tracking-[0.18em] uppercase font-semibold text-canvas/55"
                          data-testid={`phase-${c.code}`}
                        >
                          {c.phase}
                        </span>
                      )}
                    </div>
                    {/* Progress meter — public unlock threshold */}
                    <div className="mt-2.5">
                      <div className="h-1 rounded-full bg-canvas/10 overflow-hidden">
                        <motion.div
                          className={
                            "h-full rounded-full " +
                            ((voteCounts[c.code] || 0) >= threshold ? "bg-lime" : "bg-lime/60")
                          }
                          initial={{ width: 0 }}
                          animate={{
                            width: `${Math.min(100, ((voteCounts[c.code] || 0) / threshold) * 100)}%`,
                          }}
                          transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                        />
                      </div>
                      <div className="mt-1 flex items-center justify-between text-[9px] font-mono tracking-widest text-canvas/45">
                        <span data-testid={`votes-${c.code}`}>
                          {voteCounts[c.code] || 0} {t("corridors.votes")}
                        </span>
                        <span>/ {threshold}</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            );

            const baseCls =
              "group relative rounded-2xl p-4 border transition-all text-left " +
              (isMine
                ? "border-lime/60 bg-lime/[0.08] shadow-[0_0_28px_-8px_rgba(232,255,0,0.55)]"
                : c.active
                  ? "border-canvas/15 bg-canvas/[0.04] hover:bg-canvas/[0.08]"
                  : alreadyVoted
                    ? "border-lime/35 bg-lime/[0.05]"
                    : "border-canvas/8 hover:border-canvas/25 hover:bg-canvas/[0.04] cursor-pointer");

            if (c.active) {
              return (
                <Link
                  key={c.code}
                  to={prefix(c.slug ? `/corridors/${c.slug}` : "/countries")}
                  className={baseCls}
                  data-testid={`corridor-${c.code}`}
                >
                  {tile}
                </Link>
              );
            }
            return (
              <motion.button
                key={c.code}
                onClick={() => castVote(c)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (i % 12) * 0.025, ease: [0.76, 0, 0.24, 1] }}
                whileTap={{ scale: 0.97 }}
                className={baseCls}
                disabled={alreadyVoted}
                data-testid={`corridor-${c.code}`}
                aria-label={alreadyVoted ? `You voted for ${c.name}` : `Vote for ${c.name}`}
              >
                {tile}
                {alreadyVoted && (
                  <span className="absolute bottom-2 right-2 text-lime">
                    <Check className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 text-canvas/55 text-sm">
          <div>
            {t("corridors.notSeeing")}{" "}
            <a
              href="mailto:corridors@instantxfer.com"
              className="text-lime underline underline-offset-2 hover:text-canvas transition-colors"
              data-testid="corridors-request"
            >
              {t("corridors.requestIt")}
            </a>
            {t("corridors.requestTail")}
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-canvas/45">
            {t("corridors.footer")}
          </div>
        </div>
      </div>
    </section>
  );
}
